import React from 'react';
import { formatCurrency } from '../utils/payrollCalculations';

interface StaffMember {
  id: number;
  name: string;
  email: string;
  nrcNumber?: string;
  role?: string;
  jobTitle?: string;
}

interface PayrollPeriod {
  id: number;
  name: string;
  startDate: string;
  endDate: string;
}

interface PayrollRecord {
  id: number;
  teacherId: number;
  periodId: number;
  baseSalary: number;
  housingAllowance: number;
  transportAllowance: number;
  responsibilityAllowance: number;
  otherAllowances: number;
  grossSalary: number;
  totalDeductions: number;
  netSalary: number;
  napsaDeduction: number;
  payeDeduction: number;
  nhimaDeduction: number;
  personalLevyDeduction?: number;
  otherDeductions?: number;
  status: string;
  paymentDate: string | null;
  createdAt: string;
  staffMember?: StaffMember;
  teacher?: StaffMember;
  period?: PayrollPeriod;
}

interface PayrollTableProps {
  payrolls: PayrollRecord[];
  page: number;
  totalPages: number;
  totalRecords: number;
  onPageChange: (page: number) => void;
  onEdit: (payroll: PayrollRecord) => void;
  onDelete: (id: number) => void;
  onBulkDelete: (ids: number[]) => void;
  onMarkAsPaid: (id: number) => void;
  onApprove: (id: number) => void;
  onViewPayslip: (payroll: PayrollRecord) => void;
  onRefresh: () => void;
}

const PayrollTable: React.FC<PayrollTableProps> = ({
  payrolls,
  page,
  totalPages,
  totalRecords,
  onPageChange,
  onEdit,
  onDelete,
  onBulkDelete,
  onMarkAsPaid,
  onApprove,
  onViewPayslip,
  onRefresh
}) => {
  const [selectedIds, setSelectedIds] = React.useState<number[]>([]);

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-ZM');
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      paid: 'bg-green-100 text-green-800',
      approved: 'bg-blue-100 text-blue-800',
      generated: 'bg-yellow-100 text-yellow-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === payrolls.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(payrolls.map(p => p.id));
    }
  };

  const toggleSelect = (id: number) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleBulkDelete = () => {
    onBulkDelete(selectedIds);
    setSelectedIds([]);
  };

  return (
    <div className="bg-white rounded-lg shadow border">
      {selectedIds.length > 0 && (
        <div className="p-4 bg-red-50 border-b flex items-center justify-between">
          <p className="text-sm text-red-700 font-medium">
            {selectedIds.length} records selected
          </p>
          <button
            onClick={handleBulkDelete}
            className="bg-red-600 text-white px-4 py-2 rounded text-sm hover:bg-red-700 font-bold"
          >
            Delete Selected
          </button>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedIds.length === payrolls.length && payrolls.length > 0}
                  onChange={toggleSelectAll}
                  className="rounded text-blue-600"
                />
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Employee</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">NRC</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Period</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Gross</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Deductions</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Net Salary</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {payrolls.map(payroll => {
              const rawRole =
                payroll.staffMember?.role ||
                payroll.teacher?.role ||
                (payroll as any).teacherRole ||
                (payroll as any).role ||
                (payroll as any).employeeRole ||
                '';
              const roleKey = String(rawRole).toLowerCase() || 'employee';
              const roleLabels: Record<string, string> = {
                headteacher: 'Headteacher',
                teacher: 'Teacher',
                admin: 'Admin',
                superadmin: 'Super Admin',
                accountant: 'Accountant',
                bursar: 'Bursar',
                hr: 'HR',
                librarian: 'Librarian',
                employee: 'Employee'
              };
              const roleLabel = roleLabels[roleKey] || roleKey.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
              const member = payroll.staffMember || payroll.teacher;
              const displayRoleLabel = roleKey === 'worker' ? (member?.jobTitle || 'Worker') : roleLabel;
              const roleBadgeClass =
                roleKey === 'headteacher'
                  ? 'bg-purple-100 text-purple-700'
                  : roleKey === 'teacher'
                    ? 'bg-sky-100 text-sky-700'
                    : roleKey === 'worker'
                      ? 'bg-emerald-100 text-emerald-700'
                    : roleKey === 'admin' || roleKey === 'superadmin'
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-slate-100 text-slate-700';
              return (
              <tr key={payroll.id} className={`hover:bg-gray-50 ${selectedIds.includes(payroll.id) ? 'bg-blue-50' : ''}`}>
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(payroll.id)}
                    onChange={() => toggleSelect(payroll.id)}
                    className="rounded text-blue-600"
                  />
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium">{member?.name || 'Unknown'}</p>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase font-semibold ${roleBadgeClass}`}>
                      {displayRoleLabel}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500">{member?.email}</p>
                </td>
                <td className="px-4 py-3 text-sm">
                  {member?.nrcNumber || 'N/A'}
                </td>
                <td className="px-4 py-3">
                  <p className="font-medium">{payroll.period?.name || 'Unknown'}</p>
                  <p className="text-sm text-gray-500">
                    {payroll.period ? formatDate(payroll.period.startDate) : '-'}
                  </p>
                </td>
                <td className="px-4 py-3 font-medium text-green-600">
                  {formatCurrency(Number(payroll.grossSalary || 0))}
                </td>
                <td className="px-4 py-3 text-red-600">
                  <div className="font-medium">
                    ({formatCurrency(Number(payroll.totalDeductions || 0))})
                  </div>
                  <div className="text-xs text-gray-500">
                    NAPSA {formatCurrency(Number(payroll.napsaDeduction || 0))} ·
                    PAYE {formatCurrency(Number(payroll.payeDeduction || 0))} ·
                    NHIMA {formatCurrency(Number(payroll.nhimaDeduction || 0))} ·
                    Levy {formatCurrency(Number(payroll.personalLevyDeduction || 0))} ·
                    Other {formatCurrency(Number(payroll.otherDeductions || 0))}
                  </div>
                </td>
                <td className="px-4 py-3 font-bold text-blue-600">
                  {formatCurrency(Number(payroll.netSalary || 0))}
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded text-sm ${getStatusBadge(payroll.status)}`}>
                    {payroll.status === 'paid'
                      ? 'Paid'
                      : payroll.status === 'approved'
                        ? 'Approved'
                        : payroll.status === 'generated'
                          ? 'Pending'
                          : payroll.status}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => onViewPayslip(payroll)}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      View
                    </button>
                    {payroll.status === 'generated' && (
                      <button
                        onClick={() => onApprove(payroll.id)}
                        className="text-green-600 hover:text-green-800 text-sm"
                      >
                        Approve
                      </button>
                    )}
                    {payroll.status !== 'paid' && (
                      <button
                        onClick={() => onMarkAsPaid(payroll.id)}
                        className="text-purple-600 hover:text-purple-800 text-sm"
                      >
                        Mark Paid
                      </button>
                    )}
                    {payroll.status === 'generated' && (
                      <>
                        <button
                          onClick={() => onEdit(payroll)}
                          className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => onDelete(payroll.id)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            )})}
          </tbody>
        </table>
      </div>

      {payrolls.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No payroll records found.
        </div>
      )}

      {/* Pagination Controls */}
      <div className="px-4 py-3 border-t flex items-center justify-between bg-gray-50">
        <div className="text-sm text-gray-600">
          Showing <span className="font-semibold">{payrolls.length}</span> of <span className="font-semibold">{totalRecords}</span> records
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => onPageChange(page - 1)}
            disabled={page === 1}
            className="px-3 py-1 border rounded bg-white hover:bg-gray-50 disabled:opacity-50 text-sm"
          >
            Previous
          </button>
          <div className="flex items-center px-2 text-sm">
            Page {page} of {totalPages || 1}
          </div>
          <button
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
            className="px-3 py-1 border rounded bg-white hover:bg-gray-50 disabled:opacity-50 text-sm"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default PayrollTable;
